import React, { useState, useMemo, useEffect } from "react";
import { format, parseISO } from "date-fns";
import { it as itLocale, enUS as enLocale } from "date-fns/locale";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  CheckCircle, Circle, WarningCircle, CaretDown, CaretUp,
  ArrowsDownUp, Plus, PencilSimple, Trash, ArrowSquareOut, CalendarBlank,
} from "@phosphor-icons/react";
import CustomItemDialog from "./CustomItemDialog";
import TaskDetailsPanel from "@/components/TaskDetailsPanel";
import SubtasksPanel from "./SubtasksPanel";
import DateField from "@/components/DateField";
import { useApp } from "@/context/AppContext";

// Self-contained checklist section used for both DOCUMENTI (docs) and VACCINAZIONI (vax).
// The rendering is identical to the previous inline block; the only new bit is the
// custom item add/edit/delete flow.
export default function ChecklistSection({
  t,
  title,
  testidPrefix,          // "checklist" | "vax"
  templateId,            // "tmpl_predep_us" | "tmpl_vax_us"
  items,                 // list from GET /api/checklists/my or /api/vaccinations/my
  urgencyFor,            // (item) => "urgent" | "soon" | "ok" | null
  onToggleItem,          // (item) => void
  onSetDeadline,         // (item, isoString | null) => void
  onRefresh,             // () => Promise<void>  refetch list + phase state
  introBlock,            // optional ReactNode rendered above the section
  onEditProfile,         // () => void — opens the departure-profile edit form
  isAnonymous,           // true when user is not logged in — mutations are blocked
  onAnonymousMutation,   // (reason) => void — invoked instead of the mutation
}) {
  const { lang } = useApp();
  const locale = lang === "en" ? enLocale : itLocale;
  // Default sort is "desc" (farthest-from-departure first) so the tasks the
  // student should tackle FIRST in the journey — the "18-15 months ahead"
  // ones — appear on top. The calendar timestamp used for sorting is derived
  // from `days_before_departure` (`nowMs + days * ONE_DAY_MS`) or from a
  // user-set deadline / subtask deadline if any, so the ordering is the real
  // calendar-date order, not a numeric-range order.
  const [sort, setSort] = useState("desc");
  const [expanded, setExpanded] = useState(() => new Set());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null); // null = create mode

  const toggleExpanded = (id) => setExpanded(prev => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  // Compute the earliest deadline coming from a subtask, if any. Returns
  // `{ ts, subtask }` so we can render a "next action" hint below the title.
  const earliestSubtask = (it) => {
    const subs = (it.subtasks || []).filter(s => !s.checked && s.deadline);
    if (!subs.length) return null;
    const withTs = subs
      .map(s => ({ s, ts: Date.parse(s.deadline) }))
      .filter(x => !Number.isNaN(x.ts))
      .sort((a, b) => a.ts - b.ts);
    return withTs[0] || null;
  };

  const sortedItems = useMemo(() => {
    const nowMs = Date.now();
    const enriched = items.map(it => {
      const own = it.deadline ? Date.parse(it.deadline) : null;
      const fromDays = own == null && typeof it.days_before_departure === "number"
        ? nowMs + it.days_before_departure * 86_400_000
        : null;
      const sub = earliestSubtask(it);
      // Effective deadline = min(own || fromDays, subtask earliest)
      const candidates = [own, fromDays, sub ? sub.ts : null].filter(v => v != null && !Number.isNaN(v));
      const effective = candidates.length ? Math.min(...candidates) : null;
      const drivenBySubtask = sub && effective === sub.ts && (own == null || sub.ts < own);
      return { it, effective, nextAction: drivenBySubtask ? sub.s : null };
    });
    enriched.sort((a, b) => {
      if (a.effective == null && b.effective == null) return 0;
      if (a.effective == null) return 1;
      if (b.effective == null) return -1;
      return sort === "asc" ? a.effective - b.effective : b.effective - a.effective;
    });
    return enriched;
  }, [items, sort]);

  const doneCount = items.filter(i => i.checked).length;

  const createCustom = async (payload) => {
    if (isAnonymous) { onAnonymousMutation?.(t.common.authReasonAddCustom); return; }
    await api.post("/checklists/custom", { template_id: templateId, ...payload });
    await onRefresh();
  };
  const updateCustom = async (itemId, payload) => {
    if (isAnonymous) { onAnonymousMutation?.(t.common.authReasonEditCustom); return; }
    await api.patch(`/checklists/custom/${itemId}`, payload);
    await onRefresh();
  };
  const deleteCustom = async (itemId) => {
    if (isAnonymous) { onAnonymousMutation?.(t.common.authReasonDeleteCustom); return; }
    // eslint-disable-next-line no-alert
    if (!window.confirm(t.modes.organizzativa.customFormDeleteConfirm)) return;
    await api.delete(`/checklists/custom/${itemId}`);
    await onRefresh();
  };

  const openCreate = () => {
    if (isAnonymous) { onAnonymousMutation?.(t.common.authReasonAddCustom); return; }
    setEditingItem(null); setDialogOpen(true);
  };
  const openEdit = (item) => {
    if (isAnonymous) { onAnonymousMutation?.(t.common.authReasonEditCustom); return; }
    setEditingItem({
      item_id: item.id,
      text: item.text,
      deadline: item.deadline,
      why: item.details?.why || "",
      how: item.details?.how || "",
      warning: item.details?.warning || "",
    });
    setDialogOpen(true);
  };

  return (
    <section data-testid={`${testidPrefix}-section`} className="space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-baseline gap-3 flex-wrap">
          <h3
            data-testid={`${testidPrefix}-section-title`}
            className="font-display font-extrabold text-xl text-ink tracking-tight"
          >
            {title}
          </h3>
        </div>
        <div
          data-testid={`${testidPrefix}-sort-toggle`}
          role="group"
          className="inline-flex items-center gap-1 rounded-full border border-border bg-surface-card p-1"
        >
          <ArrowsDownUp size={14} weight="duotone" className="text-ink-soft ml-2 mr-0.5 shrink-0" />
          <button
            type="button"
            data-testid={`${testidPrefix}-sort-asc`}
            onClick={() => setSort("asc")}
            aria-pressed={sort === "asc"}
            className={`text-xs font-sans font-semibold rounded-full px-3 py-1 transition-colors ${
              sort === "asc" ? "bg-terracotta text-white" : "text-ink-soft hover:text-ink"
            }`}
          >
            {t.modes.organizzativa.checklistSortAsc}
          </button>
          <button
            type="button"
            data-testid={`${testidPrefix}-sort-desc`}
            onClick={() => setSort("desc")}
            aria-pressed={sort === "desc"}
            className={`text-xs font-sans font-semibold rounded-full px-3 py-1 transition-colors ${
              sort === "desc" ? "bg-terracotta text-white" : "text-ink-soft hover:text-ink"
            }`}
          >
            {t.modes.organizzativa.checklistSortDesc}
          </button>
        </div>
      </div>

      {introBlock}

      <div className="space-y-2">
        {sortedItems.map(({ it: item, nextAction }) => {
          const isExpanded = expanded.has(item.id);
          const details = item.details || {};
          const hasDetails = !!(details.why || details.how || details.warning);
          const hasSubtasks = (item.subtasks || []).length > 0;
          const canExpand = hasDetails || hasSubtasks;
          const urgency = urgencyFor(item);
          const urgencyBar =
            urgency === "urgent" ? "bg-terracotta"
            : urgency === "soon" ? "bg-sand"
            : urgency === "ok"   ? "bg-sage"
            : "bg-transparent";
          const nextActionLabel = nextAction
            ? `${t.modes.organizzativa.nextActionLabel} ${nextAction.title}, ${format(parseISO(nextAction.deadline), "d MMM", { locale })}`
            : null;
          const isLinked = !!item.linked_profile_field;
          const isSelection = !!(item.selection_options && item.selection_options.length);
          const isUserInput = !!item.user_input;
          const isSpecial = isLinked || isSelection || isUserInput;
          return (
            <div
              key={item.id}
              data-testid={`${testidPrefix}-item-${item.id}`}
              data-urgency={urgency || "none"}
              data-kind={isLinked ? "linked" : isSelection ? "selection" : isUserInput ? "input" : "regular"}
              className="relative rounded-2xl bg-surface-card border border-border overflow-hidden pl-1.5"
            >
              <span
                aria-hidden="true"
                data-testid={`${testidPrefix}-urgency-${item.id}`}
                className={`absolute left-0 top-0 bottom-0 w-1.5 ${urgencyBar} ${item.checked ? "opacity-40" : ""}`}
              />
              <div className="flex items-start gap-3 p-4">
                <button
                  type="button"
                  onClick={async () => {
                    // Anon: always fire the auth prompt.
                    if (isAnonymous) { onAnonymousMutation?.(t.common.authReasonToggleTask); return; }
                    // Regular tasks: standard toggle (works both ways).
                    if (!isSpecial) { onToggleItem(item); return; }
                    // Linked: only editable via the profile form (see button
                    // below), circle stays inert on click for registered.
                    if (isLinked) return;
                    // Selection/UserInput: circle click acts as "clear" so
                    // registered users can un-check a task by wiping the
                    // underlying value (derived_checked then flips to false
                    // on the next fetch). No-op if already unchecked — the
                    // dropdown/input is the way to check it back on.
                    if (!item.checked) return;
                    const payload = { template_id: item._templateId || templateId, item_id: item.id };
                    if (isSelection) payload.selection = null;
                    if (isUserInput) payload.user_input_value = null;
                    await api.post("/checklists/select-option", payload);
                    await onRefresh();
                  }}
                  disabled={
                    // Linked stays disabled (owned by profile). Selection/
                    // UserInput are clickable when checked (to clear) but
                    // disabled when unchecked (must use the control below).
                    !isAnonymous && (
                      isLinked ||
                      ((isSelection || isUserInput) && !item.checked)
                    )
                  }
                  aria-label={isSpecial ? undefined : "toggle"}
                  className={`mt-0.5 shrink-0 ${
                    !isAnonymous && (isLinked || ((isSelection || isUserInput) && !item.checked))
                      ? "cursor-default"
                      : ""
                  }`}
                  data-testid={`${testidPrefix}-toggle-${item.id}`}
                >
                  {item.checked
                    ? <CheckCircle size={26} weight="duotone" className="text-sage" />
                    : <Circle size={26} weight="regular" className="text-ink-soft/50" />}
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start gap-2 flex-wrap">
                    <div className={`font-sans font-semibold text-ink ${item.checked ? "opacity-60" : ""}`}>
                      {item.text}
                    </div>
                    {item.custom && (
                      <span className="text-[9px] uppercase tracking-widest font-sans font-bold text-terracotta bg-terracotta/10 px-2 py-0.5 rounded-full">
                        {t.modes.organizzativa.customBadge}
                      </span>
                    )}
                  </div>
                  {item.hint && (
                    <p className="text-xs text-ink-soft mt-1 leading-relaxed">{item.hint}</p>
                  )}
                  {nextActionLabel && (
                    <p
                      data-testid={`${testidPrefix}-next-action-${item.id}`}
                      className="text-[11px] font-sans font-semibold text-terracotta mt-1.5 flex items-center gap-1"
                    >
                      <span className="inline-block w-1.5 h-1.5 rounded-full bg-terracotta" aria-hidden="true" />
                      {nextActionLabel}
                    </p>
                  )}

                  {/* Kind-specific control row: linked profile / selection / user input */}
                  {isSpecial && item.deadline_hint && (
                    <p
                      className="text-[10px] uppercase tracking-wider font-sans text-ink-soft/70 mt-2"
                      data-testid={`${testidPrefix}-deadline-hint-${item.id}`}
                    >
                      ≈ {item.deadline_hint}
                    </p>
                  )}
                  {isLinked && (
                    <LinkedProfileControl
                      item={item}
                      t={t}
                      testidPrefix={testidPrefix}
                      onEditProfile={onEditProfile}
                    />
                  )}
                  {isSelection && (
                    <SelectionControl
                      item={item}
                      t={t}
                      testidPrefix={testidPrefix}
                      templateId={templateId}
                      onRefresh={onRefresh}
                      isAnonymous={isAnonymous}
                      onAnonymousMutation={onAnonymousMutation}
                    />
                  )}
                  {isUserInput && (
                    <UserInputControl
                      item={item}
                      t={t}
                      testidPrefix={testidPrefix}
                      templateId={templateId}
                      onRefresh={onRefresh}
                      isAnonymous={isAnonymous}
                      onAnonymousMutation={onAnonymousMutation}
                    />
                  )}

                  {/* Regular tasks: deadline picker + optional indicative hint */}
                  {!isSpecial && (
                    <>
                      {item.deadline_hint && (
                        <p
                          className="text-[10px] uppercase tracking-wider font-sans text-ink-soft/70 mt-2"
                          data-testid={`${testidPrefix}-deadline-hint-${item.id}`}
                        >
                          ≈ {item.deadline_hint}
                        </p>
                      )}
                      <div className="mt-2 flex items-center gap-2 flex-wrap">
                        <label className="text-xs text-ink-soft font-sans">{t.modes.organizzativa.deadline}:</label>
                        {isAnonymous ? (
                          <button
                            type="button"
                            data-testid={`${testidPrefix}-deadline-${item.id}`}
                            onClick={() => onAnonymousMutation?.(t.common.authReasonSetDeadline)}
                            className="inline-flex items-center gap-1.5 text-xs font-sans px-2.5 py-1 rounded-lg border border-border bg-surface text-ink-soft hover:border-terracotta/50 hover:text-ink transition-colors"
                          >
                            <CalendarBlank size={13} weight="duotone" className="shrink-0 text-terracotta" />
                            <span>{t.modes.organizzativa.addDeadline}</span>
                          </button>
                        ) : (
                          <DateField
                            size="sm"
                            value={item.deadline}
                            onChange={(iso) => onSetDeadline(item, iso)}
                            testid={`${testidPrefix}-deadline-${item.id}`}
                          />
                        )}
                      </div>
                    </>
                  )}
                  <div className="mt-3 flex items-center gap-3 flex-wrap">
                    {canExpand && (
                      <button
                        type="button"
                        onClick={() => toggleExpanded(item.id)}
                        data-testid={`${testidPrefix}-learn-more-${item.id}`}
                        aria-expanded={isExpanded}
                        className="inline-flex items-center gap-1.5 text-sm font-sans font-semibold text-terracotta hover:text-terracotta-hover transition-colors"
                      >
                        {isExpanded ? t.modes.organizzativa.checklistShowLess : t.modes.organizzativa.checklistLearnMore}
                        {isExpanded ? <CaretUp size={14} weight="bold" /> : <CaretDown size={14} weight="bold" />}
                      </button>
                    )}
                    {item.custom && (
                      <>
                        <button
                          type="button"
                          data-testid={`${testidPrefix}-edit-${item.id}`}
                          onClick={() => openEdit(item)}
                          className="inline-flex items-center gap-1 text-xs font-sans font-semibold text-ink-soft hover:text-ink transition-colors"
                        >
                          <PencilSimple size={13} weight="duotone" />
                          {t.modes.organizzativa.customFormEditAction}
                        </button>
                        <button
                          type="button"
                          data-testid={`${testidPrefix}-delete-${item.id}`}
                          onClick={() => deleteCustom(item.id)}
                          className="inline-flex items-center gap-1 text-xs font-sans font-semibold text-ink-soft/70 hover:text-terracotta transition-colors"
                        >
                          <Trash size={13} weight="duotone" />
                          {t.modes.organizzativa.customFormDeleteAction}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
              {isExpanded && (
                <div
                  data-testid={`${testidPrefix}-details-${item.id}`}
                  className="px-4 pb-5 pt-3 space-y-3 border-t border-border/40 mx-4"
                >
                  <TaskDetailsPanel
                    why={details.why}
                    how={details.how}
                    careful={details.warning}
                    whyLabel={t.modes.organizzativa.checklistDetailsWhy}
                    howLabel={t.modes.organizzativa.checklistDetailsHow}
                    carefulLabel={t.modes.organizzativa.checklistDetailsWarning}
                  />
                  <SubtasksPanel
                    t={t}
                    parentItem={item}
                    templateId={templateId}
                    testidPrefix={testidPrefix}
                    onRefresh={onRefresh}
                    isAnonymous={isAnonymous}
                    onAnonymousMutation={onAnonymousMutation}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="pt-1">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={openCreate}
          data-testid={`${testidPrefix}-add-custom`}
          className="rounded-full border-terracotta text-terracotta hover:bg-terracotta/5 font-sans font-semibold"
        >
          <Plus size={14} weight="bold" className="mr-1" />
          {t.modes.organizzativa.addCustomItem}
        </Button>
      </div>

      <CustomItemDialog
        t={t}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        mode={editingItem ? "edit" : "create"}
        initial={editingItem}
        onSubmit={editingItem
          ? (payload) => updateCustom(editingItem.item_id, payload)
          : createCustom}
      />
    </section>
  );
}


// ────────────────────────────────────────────────────────────────
// Sub-components for the three "special" task kinds
// ────────────────────────────────────────────────────────────────

function LinkedProfileControl({ item, t, testidPrefix, onEditProfile }) {
  return (
    <div
      className="mt-3 flex items-center gap-2 flex-wrap rounded-xl bg-surface/60 border border-border/60 px-3 py-2"
      data-testid={`${testidPrefix}-linked-${item.id}`}
    >
      <span className="text-[11px] uppercase tracking-widest text-ink-soft/70 font-sans font-semibold">
        {t.modes.organizzativa.linkedProfileLabel}
      </span>
      <span className="text-sm font-sans font-semibold text-ink">
        {item.linked_profile_value || "—"}
      </span>
      {onEditProfile && (
        <button
          type="button"
          onClick={() => onEditProfile(item.linked_profile_field)}
          data-testid={`${testidPrefix}-linked-edit-${item.id}`}
          className="ml-auto inline-flex items-center gap-1 text-xs font-sans font-semibold text-terracotta hover:text-terracotta-hover transition-colors"
        >
          <ArrowSquareOut size={13} weight="duotone" />
          {t.modes.organizzativa.linkedProfileEdit}
        </button>
      )}
    </div>
  );
}

function SelectionControl({ item, t, testidPrefix, templateId, onRefresh, isAnonymous, onAnonymousMutation }) {
  const save = async (value) => {
    if (isAnonymous) { onAnonymousMutation?.(t.common.authReasonSelectOption); return; }
    await api.post("/checklists/select-option", {
      template_id: templateId,
      item_id: item.id,
      selection: value || null,
    });
    await onRefresh();
  };
  // For anonymous users we render a plain gated button in place of the
  // Select — clicking it fires the popup immediately instead of first
  // opening the dropdown, mirroring the DateField pattern.
  if (isAnonymous) {
    const currentLabel = (item.selection_options || []).find(o => o.value === item.selection)?.label
      || t.modes.organizzativa.selectionPh;
    return (
      <div className="mt-3" data-testid={`${testidPrefix}-selection-${item.id}`}>
        <button
          type="button"
          data-testid={`${testidPrefix}-selection-trigger-${item.id}`}
          onClick={() => onAnonymousMutation?.(t.common.authReasonSelectOption)}
          className="h-10 w-full rounded-xl bg-surface font-sans text-sm border border-border px-3 flex items-center justify-between text-ink-soft hover:border-terracotta/50 hover:text-ink transition-colors"
        >
          <span className="truncate">{currentLabel}</span>
          <CaretDown size={14} weight="bold" className="shrink-0 opacity-60" />
        </button>
      </div>
    );
  }
  return (
    <div className="mt-3" data-testid={`${testidPrefix}-selection-${item.id}`}>
      <Select value={item.selection || ""} onValueChange={save}>
        <SelectTrigger
          data-testid={`${testidPrefix}-selection-trigger-${item.id}`}
          className="h-10 rounded-xl bg-surface font-sans text-sm"
        >
          <SelectValue placeholder={t.modes.organizzativa.selectionPh} />
        </SelectTrigger>
        <SelectContent>
          {(item.selection_options || []).map(opt => (
            <SelectItem
              key={opt.value}
              value={opt.value}
              data-testid={`${testidPrefix}-selection-opt-${item.id}-${opt.value}`}
            >
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function UserInputControl({ item, t, testidPrefix, templateId, onRefresh, isAnonymous, onAnonymousMutation }) {
  const [draft, setDraft] = useState(item.user_input_value ?? "");
  // Keep the local draft in sync with the server-side value so clearing the
  // task via the circle click (which POSTs user_input_value=null) also
  // visually empties the input without needing a full page reload.
  useEffect(() => {
    setDraft(item.user_input_value ?? "");
  }, [item.user_input_value]);
  const kind = item.user_input?.kind || "text";
  const unit = item.user_input?.unit || "";
  const placeholder = item.user_input?.placeholder || t.modes.organizzativa.userInputPh;
  const commit = async () => {
    const v = String(draft).trim();
    if (v === (item.user_input_value ?? "")) return;
    await api.post("/checklists/select-option", {
      template_id: templateId,
      item_id: item.id,
      user_input_value: v || null,
    });
    await onRefresh();
  };
  // Anon: replace the editable input with a plain gated button that fires
  // the popup on click. This ensures the popup is triggered up-front (not
  // only on blur, which some users never trigger).
  if (isAnonymous) {
    const currentValue = item.user_input_value ?? "";
    return (
      <div
        className="mt-3 flex items-center gap-2"
        data-testid={`${testidPrefix}-input-${item.id}`}
      >
        <button
          type="button"
          data-testid={`${testidPrefix}-input-field-${item.id}`}
          onClick={() => onAnonymousMutation?.(t.common.authReasonSaveInput)}
          className="h-10 rounded-xl font-sans text-sm max-w-[220px] w-full px-3 border border-border bg-surface text-left text-ink-soft hover:border-terracotta/50 hover:text-ink transition-colors truncate"
        >
          {currentValue || placeholder}
        </button>
        {unit && (
          <span className="text-xs font-sans text-ink-soft">{unit}</span>
        )}
      </div>
    );
  }
  return (
    <div
      className="mt-3 flex items-center gap-2"
      data-testid={`${testidPrefix}-input-${item.id}`}
    >
      <Input
        type={kind === "number" ? "number" : "text"}
        inputMode={kind === "number" ? "numeric" : undefined}
        value={draft}
        placeholder={placeholder}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); } }}
        data-testid={`${testidPrefix}-input-field-${item.id}`}
        className="h-10 rounded-xl font-sans text-sm max-w-[220px]"
      />
      {unit && (
        <span className="text-xs font-sans text-ink-soft">{unit}</span>
      )}
    </div>
  );
}
